import {Issue, IssueLine, TimestampKeyTuple} from './types';
import {getXValueForTimestamp} from './utils';

export const initialState: IssuesByTimestampState = {
  activeFetchErrorDrawer: false,
  issues: [],
  issuesByCacheKey: {} as Record<string, IssueLine[]>,
  timestamps: {},
};

type Actions =
  | {
      type: 'fetchTimestamps-start';
      payload: {xAxis: string; cacheKey: string};
    }
  | {
      type: 'fetchTimestamps-error';
      payload: {xAxis: string; cacheKey: string; error: Error};
    }
  | {
      type: 'fetchTimestamps-success';
      payload: {
        xAxis: string;
        cacheKey: string;
        results: TimestampKeyTuple[];
      };
    }
  | {
      type: 'addIssues';
      payload: {
        since: number;
        issues: Issue[];
      };
    }
  | {
      type: 'updateIssueError';
      payload: {
        hasError: boolean;
      };
    }
  | {
      type: 'clearIssues';
    };

export function issuesByTimestampReducer(
  state: IssuesByTimestampState,
  action: Actions
) {
  switch (action.type) {
    case 'addIssues': {
      const savedIssues = (state.issues ?? [])
        .filter(issue => issue.timestamp < action.payload.since)
        .concat(action.payload.issues)
        .sort((a, b) => a.timestamp - b.timestamp)
        .map((issue, i) => ({
          ...issue,
          description:
            issue.description ??
            'During training, GPU utilization dropped while CPU and disk usage stayed high, indicating a data loading bottleneck likely due to insufficient data loader workers or slow disk reads. This reduced overall training speed.',
          id: issue.id ?? `${issue.alertName}-${issue.timestamp}-${i}`,
          linkURL:
            issue.linkURL ??
            `https://cks-grafana.coreweave.com/d/ddbdicm9sw7c5x/node-details?orgId=1&var-node=${issue.node}&from=${issue.timestamp}&to=${issue.timestamp}`,
          timestamp:
            issue.timestamp ??
            parseInt(Object.keys(issue.occurrences ?? {}).sort()[0]),
          title: issue.title ?? issue.alertName,
        }));

      return {
        ...state,
        issues: savedIssues,
      };
    }
    case 'updateIssueError': {
      return {
        ...state,
        activeFetchErrorDrawer: action.payload.hasError,
      };
    }
    case 'clearIssues': {
      return {
        ...state,
        activeFetchErrorDrawer: false,
        issues: [],
        issuesByCacheKey: {} as Record<string, IssueLine[]>,
      };
    }
    case 'fetchTimestamps-start': {
      const thisRecord = state.timestamps[action.payload.cacheKey];
      return {
        ...state,
        timestamps: {
          ...state.timestamps,
          [action.payload.cacheKey]: {
            ...thisRecord,
            error: null,
            loading: true,
            keyByTimestamp: thisRecord?.keyByTimestamp ?? [],
          },
        },
      };
    }
    case 'fetchTimestamps-error': {
      return {
        ...state,
        timestamps: {
          ...state.timestamps,
          [action.payload.cacheKey]: {
            error: action.payload.error,
            loading: false,
            keyByTimestamp: [],
          },
        },
      };
    }
    case 'fetchTimestamps-success': {
      const convertedIssuesList = convertIssuesToTimestampKeyTuples(
        state.issues,
        action.payload.results
      );
      return {
        ...state,
        issuesByCacheKey: {
          ...state.issuesByCacheKey,
          [action.payload.cacheKey]: convertedIssuesList,
        },
        timestamps: {
          ...state.timestamps,
          [action.payload.cacheKey]: {
            error: null,
            loading: false,
            keyByTimestamp: action.payload.results,
          },
        },
      };
    }

    default: {
      return state;
    }
  }
}

export function convertIssuesToTimestampKeyTuples(
  issues: Issue[],
  keyByTimestamp: TimestampKeyTuple[]
) {
  const convertedIssuesList: IssueLine[] = [];
  let searchStartIndex = 0; // Track index instead of slicing arrays

  issues.forEach(issue => {
    // Use optimized search that starts from our last position
    const result = getXValueForTimestamp(
      keyByTimestamp,
      issue.timestamp,
      searchStartIndex
    );
    if (result === undefined) {
      // this represents an issue outside the range of this zoom
      return;
    }
    searchStartIndex = result.bottomIndex; // Update search start position
    convertedIssuesList.push({
      left: 0,
      id: issue.id,
      status: result.status,
      timestamp: issue.timestamp,
      xValue: result.value,
    });
  });
  return convertedIssuesList;
}

type CacheKey = string;

type TimestampDetail = {
  error: Error | null;
  loading: boolean;
  keyByTimestamp: TimestampKeyTuple[];
};

type IssuesByTimestampState = {
  activeFetchErrorDrawer: boolean;
  issues: Issue[];
  issuesByCacheKey: Record<CacheKey, IssueLine[]>;
  timestamps: Record<CacheKey, TimestampDetail>;
};
